import { useContext, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useMutation, useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AuthContext from "../../context/AuthProvider";
import logo from "../../utils/images/ridePlus.png";
import { PROFILE_IMAGE_BASE_URL, PROFILE_IMAGE_URL } from "../../utils/config";
import { CheckCircleOutline, LockOutlined } from "@mui/icons-material";
import Error from "../error";
import useTenantBranding from "../../hooks/useTenantBranding";

function Consent({ consentId }) {
  const axiosPrivate = useAxiosPrivate();
  const { setCurrentTenant } = useContext(AuthContext);
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  // Mutation & Query
  const { isLoading, data, error } = useQuery(
    ["consent"],
    () => {
      return axiosPrivate.get(`/oauth/consent/${consentId}`);
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  const consentTenantLogo = data?.data?.data?.tenant_logo || tenantLogo;

  const {
    mutate,
    isLoading: approvalLoading,
    data: approvalData,
  } = useMutation(() => {
    return axiosPrivate.post(
      "/oauth/approveConsent",
      {
        consent_id: consentId,
      },
      {
        withCredentials: true,
      }
    );
  });

  const { isFetching: profileIsFetching, data: profile } = useQuery(
    ["user_profile"],
    () => {
      return axiosPrivate.get(`/profile`);
    },
    {
      refetchOnWindowFocus: false,
      enable: false,
    }
  );

  const {
    mutate: rejectMutate,
    isLoading: rejectLoading,
    data: rejectData,
  } = useMutation(() => {
    return axiosPrivate.post(
      "/oauth/rejectConsent",
      {
        consent_id: consentId,
        failure_reason: "User rejected to grant the scopes you requested!",
      },
      {
        withCredentials: true,
      }
    );
  });

  // USEEFFECT
  // Extract tenant information from consent data when available
  useEffect(() => {
    if (data?.data?.data?.tenant_id) {
      setCurrentTenant({
        id: data.data.data.tenant_id,
        name: data.data.data.tenant_name || null, // If tenant name is available
        logo: data.data.data.tenant_logo || null, // If tenant logo is available
      });
    }
  }, [data?.data?.data, setCurrentTenant]);

  useEffect(() => {
    if (data?.data?.data?.client_trusted) {
      mutate();
    }
  }, [data?.data?.data?.client_trusted, mutate]);
  useEffect(() => {
    if (data?.data?.data?.approved) {
      mutate();
    }
  }, [data?.data?.data?.approved, mutate]);

  useEffect(() => {
    if (approvalData?.data?.data?.location) {
      window.location.href = approvalData?.data?.data?.location;
    }
  }, [approvalData?.data?.data?.location]);

  useEffect(() => {
    if (rejectData?.data?.data?.location) {
      window.location.href = rejectData?.data?.data?.location;
    }
  }, [rejectData?.data?.data?.location]);

  // HELPERS
  const onApprove = () => {
    mutate();
  };
  const onReject = () => {
    rejectMutate();
  };

  // RENDER
  if (error) return <Error error={error} />;

  if (isLoading || data?.data?.data?.approved) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          minHeight: "40vh",
        }}
      >
        <CircularProgress
          size={40}
          sx={{
            color: "secondary.main",
            marginTop: "-12px",
            marginLeft: "-12px",
          }}
        />
      </Box>
    );
  }
  if (approvalLoading && data?.data?.data?.client_trusted) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          minHeight: "40vh",
        }}
      >
        <CircularProgress
          size={40}
          sx={{
            color: "secondary.main",
            marginTop: "-12px",
            marginLeft: "-12px",
          }}
        />
      </Box>
    );
  }
  return (
    data?.data?.data &&
    !data?.data?.data?.client_trusted && (
      <Dialog
        maxWidth="sm"
        open={true}
        sx={{
          "& .MuiDialog-paper": {
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.08)",
            borderRadius: 3,
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
          },
        }}
        PaperProps={{
          sx: {
            width: { lg: "450px", md: "450px", xs: "100%", sm: "450px" },
            maxWidth: "450px",
            height: "auto",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            background: "linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {(consentTenantLogo || !hideDefaultBranding) && (
                <Avatar
                  size="small"
                  src={consentTenantLogo || logo}
                  sx={{
                    width: 28,
                    height: 28,
                    mr: 1.5,
                    borderRadius: 1,
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#1a1a1a",
                }}
              >
                {hideDefaultBranding ? "Sign In" : "Sign In with SSO"}
              </Typography>
            </Box>
            <LockOutlined sx={{ fontSize: 20, color: "#666" }} />
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              pt: 1,
            }}
          >
            {profileIsFetching ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 2,
                  minHeight: "40vh",
                }}
              >
                <CircularProgress
                  size={40}
                  sx={{
                    color: "secondary.main",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mb: 3,
                    mt: 2,
                  }}
                >
                  <Avatar
                    alt={data?.data?.data?.client_name ?? ""}
                    sx={{
                      width: "72px",
                      height: "72px",
                      borderRadius: 2,
                      border: "2px solid #f0f0f0",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                      bgcolor: "#fafafa",
                      "& .MuiAvatar-img": {
                        objectFit: "contain",
                        p: 0.5,
                      },
                    }}
                    src={data?.data?.data?.client_logo}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                      textAlign: "center",
                      mb: 0.5,
                      color: "#1a1a1a",
                    }}
                  >
                    {data?.data?.data?.client_name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      textAlign: "center",
                      color: "#666",
                      mb: 2,
                    }}
                  >
                    wants to access your SSO Account
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Chip
                      variant="outlined"
                      avatar={
                        <Avatar
                          alt="profile_picture"
                          size="small"
                          sx={{
                            width: 28,
                            height: 28,
                          }}
                          src={`${PROFILE_IMAGE_BASE_URL}/${PROFILE_IMAGE_URL}/${profile?.data?.data?.profile_picture}`}
                        />
                      }
                      label={`${profile?.data?.data?.first_name}${
                        " " + profile?.data?.data?.middle_name
                      }`}
                      sx={{
                        px: 2,
                        py: 2.5,
                        fontWeight: 500,
                        fontSize: 14,
                        borderColor: "#e0e0e0",
                        backgroundColor: "#fafafa",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      bgcolor: "#f8f9fa",
                      p: 2.5,
                      borderRadius: 2,
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#495057",
                        mb: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      This app will be able to:
                    </Typography>
                    {data?.data?.data?.scopes?.map((item) => (
                      <Box
                        key={item.name}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 1.5,
                          "&:last-child": { mb: 0 },
                        }}
                      >
                        <CheckCircleOutline
                          sx={{
                            fontSize: 18,
                            color: "#28a745",
                            mr: 1.5,
                            mt: 0.2,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#212529",
                            lineHeight: 1.5,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
          }}
        >
          <Box
            sx={{
              display: { md: "flex", xs: "none" },
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              fullWidth
              sx={{
                py: 1.5,
                borderColor: "#dee2e6",
                color: "#495057",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                ":hover": {
                  borderColor: "#adb5bd",
                  bgcolor: "#f8f9fa",
                },
              }}
              onClick={onReject}
              disabled={rejectLoading || approvalLoading}
            >
              {rejectLoading ? <CircularProgress size={24} /> : "Cancel"}
            </Button>
            <Button
              size="small"
              variant="contained"
              type="submit"
              fullWidth
              sx={{
                py: 1.5,
                bgcolor: "primary.main",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                "&:hover": {
                  transform: { xs: "none", sm: "translateY(-1px)" },
                  boxShadow: {
                    xs: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                    sm: "0 4px 8px 2px rgba(33, 203, 243, .4)",
                  },
                },
              }}
              onClick={onApprove}
              disabled={rejectLoading || approvalLoading}
            >
              {approvalLoading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Allow Access"
              )}
            </Button>
          </Box>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              size="small"
              variant="contained"
              type="submit"
              fullWidth
              sx={{
                py: 1.5,
                bgcolor: "primary.main",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                "&:hover": {
                  transform: { xs: "none", sm: "translateY(-1px)" },
                  boxShadow: {
                    xs: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                    sm: "0 4px 8px 2px rgba(33, 203, 243, .4)",
                  },
                },
              }}
              onClick={onApprove}
              disabled={profileIsFetching || rejectLoading || approvalLoading}
            >
              {approvalLoading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Allow Access"
              )}
            </Button>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              sx={{
                py: 1.5,
                borderColor: "#dee2e6",
                color: "#495057",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                ":hover": {
                  borderColor: "#adb5bd",
                  bgcolor: "#f8f9fa",
                },
              }}
              onClick={onReject}
              disabled={rejectLoading || approvalLoading}
            >
              {rejectLoading ? <CircularProgress size={24} /> : "Cancel"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    )
  );
}
export default Consent;
